<template>
  <div v-if="hint" ref="hintWrapper" class="hint-wrapper" :class="{ 'position-absolute': absolute, 'position-left': left }">
    <button
        type="button"
        @click="toggleHint"
        class="hint-trigger"
        :class="{ 'hint-trigger--active': showHint }"
        title="Pokaż podpowiedź"
        aria-label="Pokaż podpowiedź"
    >
      ?
    </button>

    <div
        v-if="showHint"
        ref="hintBubble"
        class="hint-bubble"
        :style="{ '--hint-bubble-offset-x': `${hintBubbleOffsetX}px` }"
    >
      <div class="hint-bubble__arrow"></div>

      <div class="hint-bubble__header">
        <h3>Podpowiedź</h3>
        <button
            type="button"
            class="hint-bubble__close"
            @click="closeHint"
            aria-label="Zamknij podpowiedź"
        >
          ×
        </button>
      </div>

      <div class="hint-bubble__content">
        <template v-if="hint.steps && hint.steps.length > 0">
          <div
              v-for="(step, index) in hint.steps"
              :key="step.id"
              v-show="currentStep === index"
              class="hint-step"
          >
            <h4>{{ step.title }}</h4>

            <div v-if="step.image_path" class="hint-step__image-wrapper">
              <button
                  type="button"
                  class="hint-step__image-button"
                  @click="openImagePreview(step)"
                  :aria-label="`Powiększ zdjęcie: ${step.title}`"
              >
                <img
                    :src="getImageUrl(step.image_path)"
                    :alt="step.title"
                    class="hint-step__image"
                >
                <span class="hint-step__image-zoom-label">Kliknij, aby powiększyć</span>
              </button>
            </div>

            <div class="hint-step__text" v-html="step.content"></div>
          </div>
        </template>

        <p v-else class="hint-empty">
          Brak kroków dla tej podpowiedzi.
        </p>
      </div>

      <div v-if="hint.steps && hint.steps.length > 1" class="hint-bubble__footer">
        <button
            type="button"
            class="btn-outline"
            @click="prevStep"
            :disabled="currentStep === 0"
        >
          Poprzedni
        </button>

        <div class="hint-dots" aria-label="Nawigacja po krokach podpowiedzi">
          <button
              v-for="(_, index) in hint.steps"
              :key="index"
              type="button"
              class="hint-dot"
              :class="{ 'hint-dot--active': currentStep === index }"
              @click="currentStep = index"
              :aria-label="`Przejdź do kroku ${index + 1}`"
          ></button>
        </div>

        <button
            type="button"
            class="btn-primary"
            @click="nextStep"
            :disabled="currentStep === hint.steps.length - 1"
        >
          Następny
        </button>
      </div>

      <div v-else class="hint-bubble__footer hint-bubble__footer--single">
        <button type="button" class="btn-primary" @click="closeHint">
          Zamknij
        </button>
      </div>
    </div>

    <div
        v-if="previewImage"
        class="hint-image-preview"
        role="dialog"
        aria-modal="true"
        :aria-label="`Powiększone zdjęcie: ${previewImage.alt}`"
        @click.self="closeImagePreview"
    >
      <button
          type="button"
          class="hint-image-preview__close"
          @click.stop="closeImagePreview"
          aria-label="Zamknij powiększone zdjęcie"
      >
        <span aria-hidden="true">×</span>
      </button>

      <img
          :src="previewImage.src"
          :alt="previewImage.alt"
          class="hint-image-preview__image"
      >
    </div>
  </div>
</template>

<script setup>
import { ref, nextTick, onMounted, onBeforeUnmount, defineProps } from 'vue';
import hintService from '@/services/hint.service';

const props = defineProps({
  pageKey: {
    type: String,
    required: true
  },
  absolute: {
    type: Boolean,
    default: false
  },
  left: {
    type: Boolean,
    default: false
  }
});

const hint = ref(null);
const showHint = ref(false);
const currentStep = ref(0);
const hintWrapper = ref(null);
const hintBubble = ref(null);
const hintBubbleOffsetX = ref(0);
const previewImage = ref(null);

const fetchHint = async () => {
  try {
    const response = await hintService.getForPage(props.pageKey);
    if (response.data && response.data.length > 0) {
      hint.value = response.data[0];
    }
  } catch (error) {
    console.error('Błąd podczas pobierania podpowiedzi:', error);
  }
};

const updateHintBubblePosition = async () => {
  hintBubbleOffsetX.value = 0;

  await nextTick();

  if (!hintBubble.value) return;

  const viewportPadding = 12;
  const bubbleRect = hintBubble.value.getBoundingClientRect();

  if (bubbleRect.left < viewportPadding) {
    hintBubbleOffsetX.value = viewportPadding - bubbleRect.left;
    return;
  }

  if (bubbleRect.right > window.innerWidth - viewportPadding) {
    hintBubbleOffsetX.value = window.innerWidth - viewportPadding - bubbleRect.right;
  }
};

const toggleHint = async () => {
  currentStep.value = 0;
  showHint.value = !showHint.value;

  if (showHint.value) {
    await updateHintBubblePosition();
  }
};

const closeHint = () => {
  showHint.value = false;
  hintBubbleOffsetX.value = 0;
  closeImagePreview();
};

const nextStep = () => {
  if (currentStep.value < hint.value.steps.length - 1) {
    currentStep.value++;
  }
};

const prevStep = () => {
  if (currentStep.value > 0) {
    currentStep.value--;
  }
};

const openImagePreview = (step) => {
  previewImage.value = {
    src: getImageUrl(step.image_path),
    alt: step.title
  };
};

const closeImagePreview = () => {
  previewImage.value = null;
};

const handleClickOutside = (event) => {
  if (!hintWrapper.value || previewImage.value) return;

  if (!hintWrapper.value.contains(event.target)) {
    closeHint();
  }
};

const handleKeydown = (event) => {
  if (event.key === 'Escape') {
    if (previewImage.value) {
      closeImagePreview();
      return;
    }

    closeHint();
  }
};

const handleResize = () => {
  if (showHint.value) {
    updateHintBubblePosition();
  }
};

const getImageUrl = (path) => {
  if (!path) return '';
  if (path.startsWith('http')) return path;
  return `${process.env.VUE_APP_STORAGE_URL || ''}${path}`;
};

onMounted(() => {
  fetchHint();
  document.addEventListener('click', handleClickOutside);
  document.addEventListener('keydown', handleKeydown);
  window.addEventListener('resize', handleResize);
});

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside);
  document.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('resize', handleResize);
});
</script>

<style scoped lang="scss">
.position-absolute {
  position: absolute !important;
  right: 18px;
  top: 18px;
}

.position-left {
  left: 18px !important;
}

.hint-wrapper {
  position: relative;
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  vertical-align: middle;
}

.hint-trigger {
  width: 24px;
  height: 24px;
  border: 1px solid #047857;
  border-radius: 999px;
  background: #10b981;
  color: #ffffff;
  cursor: pointer;
  font-size: 1.35rem;
  font-weight: 800;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.hint-trigger:hover,
.hint-trigger--active {
  background: #047857;
  color: #ffffff;
}

.hint-trigger:focus {
  outline: none;
  box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.18);
}

.hint-bubble {
  position: absolute;
  z-index: 50;
  top: calc(100% + 12px);
  left: 50%;
  width: 360px;
  max-width: calc(100vw - 24px);
  transform: translateX(calc(-50% + var(--hint-bubble-offset-x, 0px)));
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 12px 30px rgba(17, 24, 39, 0.16);
  color: #1f2937;
  animation: hintFadeIn 0.18s ease-out;
}

.hint-bubble__arrow {
  position: absolute;
  top: -8px;
  left: calc(50% - var(--hint-bubble-offset-x, 0px));
  width: 14px;
  height: 14px;
  background: #ffffff;
  border-top: 1px solid #e5e7eb;
  border-left: 1px solid #e5e7eb;
  transform: translateX(-50%) rotate(45deg);
}

.hint-bubble__header {
  position: relative;
  z-index: 1;
  padding: 14px 16px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.hint-bubble__header h3 {
  margin: 0;
  font-size: 1.6rem;
  font-weight: 700;
  color: #1f2937;
}

.hint-bubble__close {
  width: 28px;
  height: 28px;
  border: none;
  border-radius: 8px;
  background: #f9fafb;
  color: #6b7280;
  cursor: pointer;
  font-size: 2rem;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
}

.hint-bubble__close:hover {
  background: #fff1f2;
  color: #dc3545;
}

.hint-bubble__content {
  max-height: 360px;
  overflow-y: auto;
  padding: 16px;
}

.hint-step {
  animation: hintStepFadeIn 0.2s ease-out;
}

.hint-step h4 {
  margin: 0 0 10px;
  color: #1f2937;
  font-size: 1.55rem;
  font-weight: 700;
}

.hint-step__image-wrapper {
  margin-bottom: 12px;
}

.hint-step__image-button {
  position: relative;
  width: 100%;
  padding: 0;
  border: none;
  border-radius: 10px;
  background: transparent;
  cursor: zoom-in;
  display: block;
  overflow: hidden;
}

.hint-step__image {
  width: 100%;
  max-height: 220px;
  object-fit: cover;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  display: block;
  transition: transform 0.2s, filter 0.2s;
}

.hint-step__image-button:hover .hint-step__image {
  transform: scale(1.03);
  filter: brightness(0.88);
}

.hint-step__image-zoom-label {
  position: absolute;
  left: 50%;
  bottom: 10px;
  transform: translateX(-50%);
  padding: 6px 10px;
  border-radius: 999px;
  background: rgba(17, 24, 39, 0.82);
  color: #ffffff;
  font-size: 1.1rem;
  font-weight: 600;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.2s;
  white-space: nowrap;
}

.hint-step__image-button:hover .hint-step__image-zoom-label {
  opacity: 1;
}

.hint-image-preview {
  position: fixed;
  z-index: 9999;
  inset: 0;
  padding: 32px;
  background: rgba(17, 24, 39, 0.88);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
  animation: hintPreviewFadeIn 0.18s ease-out;
}

.hint-image-preview__image {
  max-width: 96vw;
  max-height: 92vh;
  object-fit: contain;
  border-radius: 12px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
  cursor: default;
}

.hint-image-preview__close {
  position: fixed;
  top: 18px;
  right: 18px;
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 999px;
  background: #ffffff;
  color: #1f2937;
  cursor: pointer;
  font-size: 3rem;
  line-height: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: 0.2s;
}

.hint-image-preview__close span {
  display: inline-block;
  line-height: 1;
  transform: translateY(-1px);
}

.hint-image-preview__close:hover {
  background: #fff1f2;
  color: #dc3545;
}

.hint-step__text {
  color: #374151;
  font-size: 1.35rem;
  line-height: 1.55;
}

.hint-step__text :deep(p) {
  margin: 0 0 8px;
}

.hint-step__text :deep(p:last-child) {
  margin-bottom: 0;
}

.hint-step__text :deep(ul),
.hint-step__text :deep(ol) {
  margin: 8px 0;
  padding-left: 20px;
}

.hint-step__text :deep(a) {
  color: #2563eb;
  text-decoration: none;
}

.hint-step__text :deep(a:hover) {
  text-decoration: underline;
}

.hint-empty {
  margin: 0;
  color: #6b7280;
  font-size: 1.35rem;
  text-align: center;
}

.hint-bubble__footer {
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
  background: #f9fafb;
  border-radius: 0 0 12px 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.hint-bubble__footer--single {
  justify-content: flex-end;
}

.hint-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.hint-dot {
  width: 9px;
  height: 9px;
  border: none;
  border-radius: 999px;
  background: #d1d5db;
  cursor: pointer;
  padding: 0;
  transition: 0.2s;
}

.hint-dot:hover,
.hint-dot--active {
  background: #f97316;
}

.btn-primary,
.btn-outline {
  border-radius: 8px;
  font-size: 1.25rem;
  cursor: pointer;
  padding: 8px 12px;
  transition: 0.2s;
}

.btn-primary {
  background: #f97316;
  color: #ffffff;
  border: none;
  font-weight: 600;
}

.btn-primary:hover:not(:disabled) {
  background: #ea6c0a;
}

.btn-outline {
  background: #ffffff;
  color: #1f2937;
  border: 1px solid #d1d5db;
}

.btn-outline:hover:not(:disabled) {
  border-color: #10b981;
  color: #10b981;
}

.btn-primary:disabled,
.btn-outline:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@keyframes hintFadeIn {
  from {
    opacity: 0;
    transform: translateX(calc(-50% + var(--hint-bubble-offset-x, 0px))) translateY(-4px);
  }

  to {
    opacity: 1;
    transform: translateX(calc(-50% + var(--hint-bubble-offset-x, 0px))) translateY(0);
  }
}

@keyframes hintStepFadeIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes hintPreviewFadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

@media (max-width: 480px) {
  .hint-bubble {
    position: fixed;
    top: auto;
    left: 12px;
    right: 12px;
    bottom: 12px;
    width: auto;
    max-width: none;
    transform: none;
  }

  .hint-bubble__arrow {
    display: none;
  }

  .hint-bubble__content {
    max-height: 55vh;
  }

  .hint-image-preview {
    padding: 16px;
  }

  .hint-image-preview__close {
    top: 12px;
    right: 12px;
  }

  .hint-bubble__footer {
    flex-wrap: wrap;
  }

  .hint-dots {
    order: -1;
    width: 100%;
  }

  .btn-primary,
  .btn-outline {
    flex: 1;
    text-align: center;
  }

  @keyframes hintFadeIn {
    from {
      opacity: 0;
      transform: translateY(8px);
    }

    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style>